import React from 'react';
import { defineMessages } from 'react-intl';
import { useAlertCtaCore } from '../../hooks/useAlertCtaCore';
import { AlertCtaBanner } from './AlertCtaBanner';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

const CONSENT_SOURCE = 'srp-alert-cta';

const messages = defineMessages({
  bannerText: {
    id: 'srpAlertCta.bannerText',
    defaultMessage: "Stop checking back. We'll email you when new jobs like this are posted.",
    description: 'Short banner text inviting the user to set up a job alert for the current search',
  },
  subText: {
    id: 'srpAlertCta.subText',
    defaultMessage: 'Weekly or daily digest. Unsubscribe anytime.',
    description: 'Small supporting line under the SRP alert banner headline stating frequency and that it can be cancelled',
  },
  ctaButton: {
    id: 'srpAlertCta.ctaButton',
    defaultMessage: 'Get email alerts',
    description: 'Button that opens (or triggers) job-alert setup for the current search',
  },
  dismissLabel: {
    id: 'srpAlertCta.dismissLabel',
    defaultMessage: 'Dismiss',
    description: 'Accessibility label for the close button on the SRP alert CTA',
  },
  successText: {
    id: 'srpAlertCta.successText',
    defaultMessage: "You're set. We'll email you about jobs like this.",
    description: 'Confirmation shown once alerts are enabled for the current search',
  },
  manageLink: {
    id: 'srpAlertCta.manageLink',
    defaultMessage: 'Manage alerts',
    description: 'Link from the SRP alert success state to the saved-searches/alerts management page',
  },
  pendingText: {
    id: 'srpAlertCta.pendingText',
    defaultMessage: 'Almost there: check your inbox to confirm your email and activate alerts.',
    description: 'Shown after enabling alerts when the account email still needs confirmation',
  },
  errorText: {
    id: 'srpAlertCta.errorText',
    defaultMessage: "Something went wrong. We couldn't turn on alerts. Please try again.",
    description: 'Shown when enabling alerts for the current search fails',
  },
});

export interface SrpAlertCtaProps {
  /** Only ever shown once a real search (query or filter) is active, never on the bare landing state. */
  hasActiveSearch: boolean;
}

/**
 * Contextual "get alerted" CTA rendered on the SRP (never on the bare landing state, see
 * hasActiveSearch). Doubles as the account-creation wedge: unauthenticated users are asked for
 * an account only at the moment they've expressed intent to be alerted. Visual chrome lives in
 * AlertCtaBanner, shared with JobDetailAlertCta; this file only owns the copy and the
 * useAlertCtaCore wiring.
 */
export function SrpAlertCta({ hasActiveSearch }: SrpAlertCtaProps) {
  const formatMessage = useFormatMessage();

  const targetUrl = React.useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.pathname}${window.location.search}`;
  }, []);

  const {
    shouldRender,
    busy,
    failed,
    isAuthenticated,
    alreadyActive,
    pendingConfirmation,
    uiLocale,
    handleDismiss,
    handleAuthenticatedClick,
    handleContinueAsGuest,
  } = useAlertCtaCore({ targetUrl, surface: 'srp', consentSource: CONSENT_SOURCE });

  if (!shouldRender || !hasActiveSearch) {
    return null;
  }

  return (
    // Same "srp-results" horizontal-inset class the results grid (and the top-employers strip
    // spliced into it) uses, so this card's left/right edges line up with the cards below it
    // instead of bleeding to the full container width while everything else in the feed sits
    // inset. See hit-card.css for the actual @container padding scale.
    <div className="srp-results">
      <AlertCtaBanner
        ariaLabel={formatMessage(messages.bannerText)}
        alreadyActive={alreadyActive}
        pendingConfirmation={pendingConfirmation}
        isAuthenticated={isAuthenticated}
        busy={busy}
        failed={failed}
        uiLocale={uiLocale}
        bannerText={<Msg {...messages.bannerText} />}
        subText={<Msg {...messages.subText} />}
        successText={<Msg {...messages.successText} />}
        manageLinkLabel={formatMessage(messages.manageLink)}
        pendingText={<Msg {...messages.pendingText} />}
        toggleLabel={formatMessage(messages.ctaButton)}
        dismissLabel={formatMessage(messages.dismissLabel)}
        errorText={<Msg {...messages.errorText} />}
        onDismiss={handleDismiss}
        onAuthenticatedClick={handleAuthenticatedClick}
        onContinueAsGuest={handleContinueAsGuest}
      />
    </div>
  );
}
